// Componente ETA tecnico — mappa Leaflet piccola + countdown tempo stimato.
// Mostra la posizione del tecnico in real-time e la distanza dalla sede.
import { useEffect, useRef } from 'react'
import { Navigation, Clock, MapPin } from 'lucide-react'
import { cn } from '../../../lib/utils'

interface TechnicianETAProps {
  technicianName: string | null
  distanceKm: number | null
  etaMinutes: number | null
  lastLat: number | null
  lastLng: number | null
  className?: string
}

export function TechnicianETA({
  technicianName,
  distanceKm,
  etaMinutes,
  lastLat,
  lastLng,
  className,
}: TechnicianETAProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  // Ref per istanza mappa Leaflet — tipizzazione via any per evitare dipendenza da @types/leaflet
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null)

  useEffect(() => {
    // Leaflet richiede window — eseguito solo lato browser
    if (typeof window === 'undefined') return
    if (!mapContainerRef.current) return
    if (lastLat === null || lastLng === null) return

    // Caricamento dinamico Leaflet per evitare SSR issues
    import('leaflet').then((L) => {
      // Inizializza la mappa solo la prima volta
      if (!mapRef.current) {
        mapRef.current = L.map(mapContainerRef.current!, {
          zoomControl: false,
          attributionControl: false,
          scrollWheelZoom: false,
          dragging: false,
          doubleClickZoom: false,
        }).setView([lastLat, lastLng], 13)

        // Tile layer OpenStreetMap
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
        }).addTo(mapRef.current)

        // Marker tecnico con icona personalizzata
        const techIcon = L.divIcon({
          className: '',
          html: `<div style="
            width:36px;height:36px;
            background:#0F52BA;
            border:3px solid white;
            border-radius:50%;
            display:flex;align-items:center;justify-content:center;
            box-shadow:0 2px 8px rgba(0,0,0,.3);
            font-size:14px;color:white;font-weight:700;
          ">T</div>`,
          iconSize: [36, 36],
          iconAnchor: [18, 18],
        })

        L.marker([lastLat, lastLng], { icon: techIcon })
          .addTo(mapRef.current)
          .bindPopup(technicianName ?? 'Tecnico')
      } else {
        // Aggiorna posizione mappa se già inizializzata
        mapRef.current.setView([lastLat, lastLng], 13)
      }
    }).catch(() => {
      // Leaflet non disponibile — fallback silenzioso
    })

    return () => {
      // Non distruggere la mappa ad ogni re-render: Leaflet non gestisce bene la re-inizializzazione
    }
  }, [lastLat, lastLng, technicianName])

  const formatEta = (minutes: number | null): string => {
    if (minutes === null) return 'N/D'
    if (minutes < 60) return `${minutes} min`
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return m > 0 ? `${h}h ${m}min` : `${h}h`
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm',
        className
      )}
      role="region"
      aria-label="Posizione e ETA tecnico"
    >
      {/* Mappa */}
      {lastLat !== null && lastLng !== null ? (
        <div
          ref={mapContainerRef}
          className="h-40 w-full bg-gray-100"
          aria-label="Mappa posizione tecnico"
        />
      ) : (
        <div className="flex h-40 w-full items-center justify-center bg-gray-50">
          <MapPin className="h-8 w-8 text-gray-300" aria-hidden="true" />
        </div>
      )}

      {/* Info ETA */}
      <div className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-[var(--fc-primary)]">
            <Navigation className="h-4 w-4" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {technicianName ?? 'Tecnico assegnato'}
            </p>
            <p className="text-xs text-gray-500">sta raggiungendo la sede</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-blue-50 p-3">
            <p className="mb-1 text-xs text-gray-500">Distanza</p>
            <p className="text-lg font-bold text-[var(--fc-primary)]">
              {distanceKm !== null ? `${distanceKm.toFixed(1)} km` : 'N/D'}
            </p>
          </div>
          <div className="rounded-lg bg-green-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <Clock className="h-3 w-3 text-gray-500" aria-hidden="true" />
              <p className="text-xs text-gray-500">Arrivo stimato</p>
            </div>
            <p className="text-lg font-bold text-green-700">
              {formatEta(etaMinutes)}
            </p>
          </div>
        </div>

        <p className="mt-2 text-xs text-gray-400">
          Posizione aggiornata in tempo reale
        </p>
      </div>
    </div>
  )
}
